import AcUnit from "@mui/icons-material/AcUnit";
import Cloud from "@mui/icons-material/Cloud";
import FilterDrama from "@mui/icons-material/FilterDrama";
import Dehaze from "@mui/icons-material/Dehaze";
import Grain from "@mui/icons-material/Grain";
import NightsStay from "@mui/icons-material/NightsStay";
import Thunderstorm from "@mui/icons-material/Thunderstorm";
import Umbrella from "@mui/icons-material/Umbrella";
import WbSunny from "@mui/icons-material/WbSunny";
import { SvgIconComponent } from "@mui/icons-material";

/**
 * OpenWeather condition code -> icon.
 *
 * Codes are grouped by leading digit (see https://openweathermap.org/weather-conditions):
 * 2xx thunderstorm, 3xx drizzle, 5xx rain, 6xx snow, 7xx atmosphere, 800 clear, 80x clouds.
 * Only 800 distinguishes day from night — everything else looks the same after dark.
 *
 * MUI icons rather than OpenWeather's hosted PNGs so the card matches the rest of the
 * dashboard's iconography and loads nothing from a third-party CDN.
 */
export const weatherIcon = (code: number, isDay: boolean): SvgIconComponent => {
    if (code === 800) return isDay ? WbSunny : NightsStay;

    switch (Math.floor(code / 100)) {
        case 2:
            return Thunderstorm;
        case 3:
            return Grain;
        case 5:
            return Umbrella;
        case 6:
            return AcUnit;
        case 7:
            // Mist, haze, smoke, dust. Dehaze is the closest glyph this MUI version ships.
            return Dehaze;
        case 8:
            // 801 is "few clouds" — a lighter mark than the overcast 802-804.
            return code === 801 ? FilterDrama : Cloud;
        default:
            return Cloud;
    }
};

/** Degree suffix for the active unit. Wind is km/h for metric, mph for imperial. */
export const unitSymbols = (units: "metric" | "imperial") =>
    units === "metric"
        ? { temp: "°C", wind: "km/h" }
        : { temp: "°F", wind: "mph" };

/** Short weekday for a forecast entry's local YYYY-MM-DD date. */
export const weekdayLabel = (date: string, todayIso: string): string => {
    if (date === todayIso) return "Today";

    // Parsed as UTC on purpose: the date is already local to the forecast location, so
    // letting the browser's timezone shift it would slide the label by a day.
    const parsed = new Date(`${date}T00:00:00Z`);

    return Number.isNaN(parsed.getTime())
        ? date
        : parsed.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });
};
